// src/cmps/TextEditor/plugins/FloatingTextToolbar.jsx
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'
import { $setBlocksType } from '@lexical/selection'
import { $createHeadingNode } from '@lexical/rich-text'
import { useEffect, useCallback, useState } from 'react'
import { Bold, Italic, Underline, Heading2, Heading3, Link } from 'lucide-react'
import { useFloatingEditorPanel } from '../utils/useFloatingEditorPanel'

export function FloatingTextToolbar({ anchorElem }) {
  const [editor] = useLexicalComposerContext()
  const {
    open, // ↔ show
    setOpen, // ↔ setShow
    refs,
    floatingStyles,
    getFloatingProps,
    updateFromSelection, // השם שהחזרנו ב-hook
  } = useFloatingEditorPanel({
    placement: 'top-start',
    gap: 8,
    boundary: anchorElem?.current ?? anchorElem ?? document.body,
  })

  /*----- 1. הצגה כשיש selection -----*/
  const refresh = useCallback(() => {
    editor.getEditorState().read(() => {
      const sel = $getSelection()
      if ($isRangeSelection(sel) && !sel.isCollapsed()) {
        setOpen(true)
        updateFromSelection()
      } else {
        setOpen(false)
      }
    })
  }, [editor, setOpen, updateFromSelection])

  useEffect(() => {
    const unregister = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        refresh()
        return false
      },
      1
    )
    // כדי להתעדכן גם ב־mouse-up מחוץ ל־Lexical (כמו בדוגמה הרשמית):
    document.addEventListener('selectionchange', refresh)
    return () => {
      unregister()
      document.removeEventListener('selectionchange', refresh)
    }
  }, [editor, refresh])

  /*----- 2. פעולות עיצוב טקסט -----*/
  const applyFormat = (fmt) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, fmt)

  const setBlockType = (type) => {
    editor.update(() => {
      const sel = $getSelection()
      if ($isRangeSelection(sel)) {
        $setBlocksType(sel, () =>
          type === 'paragraph'
            ? $createParagraphNode()
            : $createHeadingNode(type)
        )
      }
    })
  }

  const openLinkPanel = () => editor.dispatchCommand('SHOW_LINK_EDITOR', null)

  /*----- 3. UI -----*/
  if (!open) return null

  return (
    <div
      ref={refs.setFloating}
      style={floatingStyles}
      {...getFloatingProps()}
      className="z-50 flex gap-2 rounded-md border border-zinc-700 bg-zinc-800 p-2 shadow-lg"
    >
      <button
        type="button"
        onClick={() => applyFormat('bold')}
        className="text-white"
      >
        <Bold size={16} />
      </button>
      <button
        type="button"
        onClick={() => applyFormat('italic')}
        className="text-white"
      >
        <Italic size={16} />
      </button>
      <button
        type="button"
        onClick={() => applyFormat('underline')}
        className="text-white"
      >
        <Underline size={16} />
      </button>

      <button
        type="button"
        onClick={() => setBlockType('paragraph')}
        className="text-white"
      >
        P
      </button>
      <button
        type="button"
        onClick={() => setBlockType('h2')}
        className="text-white"
      >
        <Heading2 size={16} />
      </button>
      <button
        type="button"
        onClick={() => setBlockType('h3')}
        className="text-white"
      >
        <Heading3 size={16} />
      </button>

      <button type="button" onClick={openLinkPanel} className="text-white">
        <Link size={16} />
      </button>
    </div>
  )
}
